import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import BrowserOnly from 'components/consumer/BrowserOnly';
import PDPView3DButton from 'components/consumer/PDP/PDPView3DButton';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import PDPFullScreenGallery from 'components/consumer/PDP/PDPFullScreenGallery';
import PDPMobileGalleryZoomIcon from 'components/consumer/PDP/PDPMobileGalleryZoomIcon';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import CSSScrollSnapCarousel from 'components/consumer/CSSScrollSnapCarousel';
import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPShownIn from '../PDPShownIn';
const CarouselSelectedIndexContext = React.createContext(0);
const PDPCarouselIntroVideoSlide = t0 => {
  const $ = _c(2);
  const {
    slideIndex,
    videoId
  } = t0;
  const selectedCarouselIndex = useContext(CarouselSelectedIndexContext);
  if (slideIndex !== selectedCarouselIndex) {
    return null;
  }
  let t1;
  if ($[0] !== videoId) {
    t1 = <PDPProductIntroVideo videoId={videoId} />;
    $[0] = videoId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
PDPCarouselIntroVideoSlide.propTypes = {
  slideIndex: PropTypes.number.isRequired,
  videoId: PropTypes.string.isRequired
};

// Return items that have a unique id or videoId field
const getUniqueCarouselItems = items => {
  const uniqueItems = [];
  const seenIds = new Set();
  items.forEach(item => {
    const id = item.id || item.videoId;
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      uniqueItems.push(item);
    }
  });
  return uniqueItems;
};
const DEFAULT_PHOTOS = [];
const PDPMobileCarousel = t0 => {
  const $ = _c(100);
  const {
    displayShownIn: t1,
    enableCrops: t2,
    headerLeft,
    isConfigFlipped: t3,
    isEditOrder: t4,
    isHero: t5,
    maxPhotos: t6,
    onAfterSlide,
    onOpenFullScreenGallery,
    onPhotoViewed,
    photos: t7,
    productName,
    rightButtons,
    showARIcon: t8,
    showDecideLaterOverlay: t9,
    showFavoriteButton: t10,
    videoId,
    zoomOnClick: t11
  } = t0;
  const displayShownIn = t1 === undefined ? false : t1;
  const enableCrops = t2 === undefined ? true : t2;
  const isConfigFlipped = t3 === undefined ? false : t3;
  const isEditOrder = t4 === undefined ? false : t4;
  const isHero = t5 === undefined ? false : t5;
  const maxPhotos = t6 === undefined ? 16 : t6;
  const photos = t7 === undefined ? DEFAULT_PHOTOS : t7;
  const showARIcon = t8 === undefined ? false : t8;
  const showDecideLaterOverlay = t9 === undefined ? false : t9;
  const showFavoriteButton = t10 === undefined ? false : t10;
  const zoomOnClick = t11 === undefined ? true : t11;
  const validPhotos = useRemoveBrokenImageUrls(photos);
  const isConfigurationGallery = validPhotos?.[0]?.type === "configuration" || validPhotos?.[0]?.type === "cylindo";
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  let t12;
  if ($[0] !== onOpenFullScreenGallery) {
    t12 = () => {
      setIsFullScreenGalleryOpen(true);
      onOpenFullScreenGallery?.();
    };
    $[0] = onOpenFullScreenGallery;
    $[1] = t12;
  } else {
    t12 = $[1];
  }
  const openFullScreenGallery = t12;
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
  let t13;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {};
    $[2] = t13;
  } else {
    t13 = $[2];
  }
  const [brokenImages, setBrokenImages] = useState(t13);
  const hasMoreThanMaxPhotos = validPhotos.length > maxPhotos;
  let t14;
  if ($[3] !== hasMoreThanMaxPhotos || $[4] !== maxPhotos || $[5] !== validPhotos || $[6] !== videoId) {
    const trimmedPhotos = hasMoreThanMaxPhotos ? validPhotos.slice(0, maxPhotos) : validPhotos;
    t14 = videoId && trimmedPhotos.length ? [trimmedPhotos[0], {
      videoId
    }, ...trimmedPhotos.slice(1)] : trimmedPhotos;
    $[3] = hasMoreThanMaxPhotos;
    $[4] = maxPhotos;
    $[5] = validPhotos;
    $[6] = videoId;
    $[7] = t14;
  } else {
    t14 = $[7];
  }
  const photosAndVideo = t14;
  const carouselPhotos = photosAndVideo;
  const standardRatio = isConfigurationGallery ? 0.6666666666666666 : 0.5;
  let t15;
  if ($[8] !== displayShownIn) {
    t15 = () => {
      if (!displayShownIn) {
        return;
      }
      scrollIntoViewUtil("[data-xc=\"PDPMobileConfigurationPhotoGalleryUI\"]", 0);
    };
    $[8] = displayShownIn;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  const scrollDownToCustomizer = t15;
  let t16;
  if ($[10] !== carouselPhotos) {
    t16 = getUniqueCarouselItems(carouselPhotos);
    $[10] = carouselPhotos;
    $[11] = t16;
  } else {
    t16 = $[11];
  }
  const uniqueCarouselPhotos = t16;
  let t17;
  if ($[12] !== onAfterSlide || $[13] !== uniqueCarouselPhotos) {
    t17 = slideIndex => {
      setSelectedCarouselIndex(slideIndex);
      onAfterSlide?.(uniqueCarouselPhotos[slideIndex]);
    };
    $[12] = onAfterSlide;
    $[13] = uniqueCarouselPhotos;
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  const handleSlideChange = t17;
  let t18;
  if ($[15] !== uniqueCarouselPhotos) {
    t18 = uniqueCarouselPhotos.findIndex(_temp);
    $[15] = uniqueCarouselPhotos;
    $[16] = t18;
  } else {
    t18 = $[16];
  }
  const videoSlideIndex = t18;
  const isVideoSlideSelected = videoSlideIndex !== -1 && selectedCarouselIndex === videoSlideIndex;
  let t19;
  if ($[17] !== brokenImages || $[18] !== displayShownIn || $[19] !== enableCrops || $[20] !== hasMoreThanMaxPhotos || $[21] !== isConfigFlipped || $[22] !== isConfigurationGallery || $[23] !== isEditOrder || $[24] !== isHero || $[25] !== openFullScreenGallery || $[26] !== productName || $[27] !== scrollDownToCustomizer || $[28] !== showDecideLaterOverlay || $[29] !== standardRatio || $[30] !== uniqueCarouselPhotos || $[31] !== validPhotos.length || $[32] !== zoomOnClick) {
    let t20;
    if ($[34] !== brokenImages || $[35] !== displayShownIn || $[36] !== enableCrops || $[37] !== hasMoreThanMaxPhotos || $[38] !== isConfigFlipped || $[39] !== isConfigurationGallery || $[40] !== isEditOrder || $[41] !== isHero || $[42] !== openFullScreenGallery || $[43] !== productName || $[44] !== scrollDownToCustomizer || $[45] !== showDecideLaterOverlay || $[46] !== standardRatio || $[47] !== uniqueCarouselPhotos.length || $[48] !== validPhotos.length || $[49] !== zoomOnClick) {
      t20 = (t21, index) => {
        const {
          src,
          id,
          type,
          cropData,
          videoId: videoId_0,
          optionId,
          upholsteryName,
          valueId
        } = t21;
        const slideKey = videoId_0 ? `video-${videoId_0}` : `${id || src || index}`;
        return {
          key: slideKey,
          slide: <RatioContainer className={classNames({
            "pt-8 px-6 pb-11": isConfigurationGallery && !enableCrops,
            "scale-x-[-1]": isConfigurationGallery && isConfigFlipped,
            "flex justify-center": brokenImages[src]
          })} ratio={enableCrops ? 1 : standardRatio}>{videoId_0 ? <PDPCarouselIntroVideoSlide slideIndex={index} videoId={videoId_0} /> : <>{displayShownIn ? <PDPShownIn isEditOrder={isEditOrder} optionId={optionId} upholsteryName={upholsteryName} valueId={valueId} layout="roundSwatchLabel" onSelection={scrollDownToCustomizer} productName={productName} showInactiveUpholstery={true} /> : null}<JBProductImage className="h-full object-contain w-full" {...getMainImageImgixPropsForPhoto({
                src,
                type,
                cropData
              }, enableCrops)} lazy={index !== 0} priority={index === 0 && isHero} htmlAttributes={index === 0 && isHero ? {
                "data-xc": "PDPMobileStudioHero"
              } : undefined} sizes="100vw" onClick={zoomOnClick ? showDecideLaterOverlay ? _temp2 : () => {
                setSelectedCarouselIndex(index);
                openFullScreenGallery();
              } : undefined} forPage="pdp" onLoad={isFail => {
                if (index === 0 && isHero) {
                  markLuxPageNavigationEnd();
                }
                if (!isFail) {
                  return;
                }
                setBrokenImages({
                  ...brokenImages,
                  [src]: true
                });
              }} /></>}{hasMoreThanMaxPhotos && index === uniqueCarouselPhotos.length - 1 && <div className="items-center bottom-0 flex justify-center left-0 pointer-events-none absolute right-0 top-0"><div className="text-lg font-bold bg-brand-lightest text-brand-dark px-4 py-3">Explore{" "}{validPhotos.length - uniqueCarouselPhotos.length}+ more photos</div></div>}</RatioContainer>
        };
      };
      $[34] = brokenImages;
      $[35] = displayShownIn;
      $[36] = enableCrops;
      $[37] = hasMoreThanMaxPhotos;
      $[38] = isConfigFlipped;
      $[39] = isConfigurationGallery;
      $[40] = isEditOrder;
      $[41] = isHero;
      $[42] = openFullScreenGallery;
      $[43] = productName;
      $[44] = scrollDownToCustomizer;
      $[45] = showDecideLaterOverlay;
      $[46] = standardRatio;
      $[47] = uniqueCarouselPhotos.length;
      $[48] = validPhotos.length;
      $[49] = zoomOnClick;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    t19 = uniqueCarouselPhotos.map(t20).filter(Boolean);
    $[17] = brokenImages;
    $[18] = displayShownIn;
    $[19] = enableCrops;
    $[20] = hasMoreThanMaxPhotos;
    $[21] = isConfigFlipped;
    $[22] = isConfigurationGallery;
    $[23] = isEditOrder;
    $[24] = isHero;
    $[25] = openFullScreenGallery;
    $[26] = productName;
    $[27] = scrollDownToCustomizer;
    $[28] = showDecideLaterOverlay;
    $[29] = standardRatio;
    $[30] = uniqueCarouselPhotos;
    $[31] = validPhotos.length;
    $[32] = zoomOnClick;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const slides = t19;
  let t20;
  if ($[51] !== handleSlideChange || $[52] !== slides) {
    t20 = <CSSScrollSnapCarousel onSlideChange={handleSlideChange} slides={slides} />;
    $[51] = handleSlideChange;
    $[52] = slides;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== selectedCarouselIndex || $[55] !== t20) {
    t21 = <CarouselSelectedIndexContext.Provider value={selectedCarouselIndex}>{t20}</CarouselSelectedIndexContext.Provider>;
    $[54] = selectedCarouselIndex;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  const t22 = !displayShownIn;
  const t23 = !showDecideLaterOverlay;
  let t24;
  if ($[57] !== displayShownIn || $[58] !== showDecideLaterOverlay || $[59] !== t22 || $[60] !== t23) {
    t24 = classNames("absolute right-4 top-4 z-[1]", {
      "justify-between left-4": t22,
      "justify-end": displayShownIn,
      flex: t23,
      "!hidden": showDecideLaterOverlay
    });
    $[57] = displayShownIn;
    $[58] = showDecideLaterOverlay;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== displayShownIn || $[63] !== headerLeft) {
    t25 = !displayShownIn ? <div className="pointer-events-none">{!!headerLeft && headerLeft}</div> : null;
    $[62] = displayShownIn;
    $[63] = headerLeft;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== showARIcon) {
    t26 = showARIcon && <PDPView3DButton />;
    $[65] = showARIcon;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== isConfigurationGallery || $[68] !== isVideoSlideSelected || $[69] !== openFullScreenGallery || $[70] !== zoomOnClick) {
    t27 = !!zoomOnClick && !isVideoSlideSelected && <PDPMobileGalleryZoomIcon inverted={isConfigurationGallery} onClick={openFullScreenGallery} />;
    $[67] = isConfigurationGallery;
    $[68] = isVideoSlideSelected;
    $[69] = openFullScreenGallery;
    $[70] = zoomOnClick;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== showFavoriteButton) {
    t28 = showFavoriteButton && <PDPFavoriteButton size="sm" className="ml-0" />;
    $[72] = showFavoriteButton;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const t29 = !!rightButtons && rightButtons;
  let t30;
  if ($[74] !== t28 || $[75] !== t29) {
    t30 = <div className="flex flex-col gap-2">{t28}{t29}</div>;
    $[74] = t28;
    $[75] = t29;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t26 || $[78] !== t27 || $[79] !== t30) {
    t31 = <div className="flex [&_>_*_+_*]:ml-2.5">{t26}{t27}{t30}</div>;
    $[77] = t26;
    $[78] = t27;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== t24 || $[82] !== t25 || $[83] !== t31) {
    t32 = <div className={t24}>{t25}{t31}</div>;
    $[81] = t24;
    $[82] = t25;
    $[83] = t31;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  let t33;
  if ($[85] !== t21 || $[86] !== t32) {
    t33 = <div className="relative">{t21}{t32}</div>;
    $[85] = t21;
    $[86] = t32;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => {
      setIsFullScreenGalleryOpen(false);
    };
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== displayShownIn || $[90] !== isEditOrder || $[91] !== isFullScreenGalleryOpen || $[92] !== onPhotoViewed || $[93] !== productName || $[94] !== selectedCarouselIndex || $[95] !== validPhotos) {
    t35 = <BrowserOnly><PDPFullScreenGallery isEditOrder={isEditOrder} isOpen={isFullScreenGalleryOpen} photos={validPhotos} initialSelectedPhotoIndex={selectedCarouselIndex} onClose={t34} onPhotoViewed={onPhotoViewed} productName={productName} displayShownIn={displayShownIn} /></BrowserOnly>;
    $[89] = displayShownIn;
    $[90] = isEditOrder;
    $[91] = isFullScreenGalleryOpen;
    $[92] = onPhotoViewed;
    $[93] = productName;
    $[94] = selectedCarouselIndex;
    $[95] = validPhotos;
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== t33 || $[98] !== t35) {
    t36 = <>{t33}{t35}</>;
    $[97] = t33;
    $[98] = t35;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  return t36;
};
PDPMobileCarousel.propTypes = {
  displayShownIn: PropTypes.bool,
  enableCrops: PropTypes.bool,
  headerLeft: PropTypes.node,
  isConfigFlipped: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isHero: PropTypes.bool,
  maxPhotos: PropTypes.number,
  onAfterSlide: PropTypes.func,
  onOpenFullScreenGallery: PropTypes.func,
  onPhotoViewed: PropTypes.func,
  onDimensionsIconClick: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    src: PropTypes.string,
    type: PropTypes.string
  })),
  productName: PropTypes.string,
  rightButtons: PropTypes.node,
  showARIcon: PropTypes.bool,
  showDecideLaterOverlay: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  videoId: PropTypes.string,
  zoomOnClick: PropTypes.bool
};
export default PDPMobileCarousel;
function _temp(item) {
  return item.videoId;
}
function _temp2() {}